import { useEffect, useMemo, useState } from 'react';
import { useOutletContext, useParams, Link } from 'react-router-dom';
import { allPlay, ownerRecords, scoreboardWeek, splitAmong, type PlayerLine } from '@ff/core';
import type { SeasonContext } from '../lib/seasonContext.ts';
import { Avatar } from '../components/Avatar.tsx';
import { fetchLineups, type StoredLineups } from '../lib/api.ts';
import { useSeasonSelect } from '../lib/seasonSelect.tsx';
import { money as fmtMoney, ordinal, points, record, signedMoney } from '../lib/format.ts';

/**
 * One manager, everything the book knows about them.
 *
 * The rest of the app is organised by week — this is the one view organised by
 * person, which is what you actually want when somebody's name comes up: what
 * they've got, what they drafted, what they're owed, and what they've done in
 * the nine years before this one.
 */
export function Manager() {
  const { ownerId = '' } = useParams();
  const { stored, nameOf, teamOf } = useOutletContext<SeasonContext>();
  const { data, money, draft, firstRound } = stored.bundle;

  const owner = data.owners.find((o) => o.id === ownerId);
  const team = data.teams.find((t) => t.ownerId === ownerId);
  const wallet = money.owners.find((o) => o.ownerId === ownerId);

  // Regular season only. The record and points-for beside these come from
  // ESPN's regular-season figures, so including playoff weeks here would print
  // a per-game average that doesn't divide into the points total next to it.
  const results = useMemo(
    () => data.results.filter((r) => r.ownerId === ownerId && r.isFinal && !r.isPlayoff),
    [data.results, ownerId],
  );
  const luck = useMemo(() => allPlay(data).find((r) => r.ownerId === ownerId), [data, ownerId]);

  if (!owner) {
    return (
      <div className="space-y-4">
        <p className="eyebrow">Not found</p>
        <p className="text-sm text-chalk-dim">
          No manager with that id this season.{' '}
          <Link to="/standings" className="text-brass hover:opacity-80">
            Back to the standings
          </Link>
          .
        </p>
      </div>
    );
  }

  const name = nameOf(ownerId);
  const best = results.length ? Math.max(...results.map((r) => r.score)) : null;
  const worst = results.length ? Math.min(...results.map((r) => r.score)) : null;
  const ppg = results.length
    ? results.reduce((n, r) => n + r.score, 0) / results.length
    : null;

  // Awards they hold or currently lead — the reason to care about this page in
  // October rather than only in January.
  const holding = money.awards.filter((a) => a.winnerIds.includes(ownerId) && a.status !== 'pending');
  const elimination = money.survivor.eliminations.find((e) => e.ownerId === ownerId);
  const stillAlive = money.survivor.alive.includes(ownerId);
  const myPicks = draft.filter((p) => p.ownerId === ownerId).sort((a, b) => a.overall - b.overall);
  const r1 = firstRound.find((e) => e.ownerId === ownerId);

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-center gap-4">
        <Avatar name={name} size={64} />
        <div className="min-w-0">
          <p className="eyebrow">{data.season} · manager</p>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight">{name}</h1>
          <p className="font-mono text-xs text-chalk-dim">
            {teamOf(ownerId) || '—'}
            {team && ` · ${record(team.wins, team.losses, team.ties)}`}
            {team?.playoffSeed ? ` · ${ordinal(team.playoffSeed)} seed` : ''}
          </p>
        </div>
      </header>

      <Section title="This season">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Points for" value={team ? points(team.pointsFor) : '—'} />
          <Stat label="Points against" value={team ? points(team.pointsAgainst) : '—'} />
          <Stat label="Per game" value={ppg === null ? '—' : points(ppg)} />
          <Stat
            label="Luck"
            value={luck ? `${luck.luck > 0 ? '+' : ''}${luck.luck.toFixed(1)}` : '—'}
            hint={
              luck
                ? `${luck.allPlayWins}-${luck.allPlayLosses}${
                    luck.allPlayTies ? `-${luck.allPlayTies}` : ''
                  } against everyone`
                : undefined
            }
          />
          <Stat label="Best week" value={best === null ? '—' : points(best)} />
          <Stat label="Worst week" value={worst === null ? '—' : points(worst)} />
          <Stat
            label="Survivor"
            value={elimination ? `Out, wk ${elimination.week}` : stillAlive ? 'Alive' : '—'}
          />
          <Stat
            label="R1 pick"
            value={r1 ? points(r1.total) : '—'}
            hint={r1 ? r1.playerName : undefined}
          />
        </div>
        {holding.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {holding.map((a) => (
              <span
                key={`${a.id}-${a.week ?? ''}`}
                className="rounded-full border border-brass/40 px-2 py-0.5 font-mono text-[0.65rem] uppercase tracking-wider text-brass"
              >
                {/* The label already names the week for per-week awards, and
                    the money is this manager's share — a tied weekly high pays
                    $15 each, not $30 each, and the wallet above says so. */}
                {a.label} · {fmtMoney(splitAmong(a.amount, a.winnerIds).get(ownerId) ?? a.amount)}
                {a.winnerIds.length > 1 ? ` (split ${a.winnerIds.length})` : ''}
                {a.status === 'live' ? ' · live' : ''}
              </span>
            ))}
          </div>
        )}
      </Section>

      {wallet && (
        <Section title="Money">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Earned" value={fmtMoney(wallet.earned)} />
            <Stat label="At stake" value={fmtMoney(wallet.atStake)} />
            {/* wallet.owed is the buy-in, set unconditionally — `boughtIn`
                is what says whether it's been handed over. Calling it "owed"
                put "Owed $300" directly above "buy-in paid". */}
            <Stat
              label="Buy-in"
              value={fmtMoney(wallet.owed)}
              hint={wallet.boughtIn ? 'paid' : 'outstanding'}
              tone={wallet.boughtIn ? undefined : 'bad'}
            />
            <Stat
              label="Net"
              value={signedMoney(wallet.net)}
              tone={wallet.net > 0 ? 'good' : wallet.net < 0 ? 'bad' : undefined}
            />
          </div>
          {wallet.paid > 0 && (
            <p className="mt-2 font-mono text-[0.65rem] uppercase tracking-wider text-chalk-dim">
              {fmtMoney(wallet.paid)} settled
            </p>
          )}
        </Section>
      )}

      <Roster season={data.season} ownerId={ownerId} data={data} />

      {myPicks.length > 0 && (
        <Section title="What they drafted">
          <div className="card divide-y divide-felt-line p-1">
            {myPicks.map((p) => (
              <div key={p.overall} className="flex items-baseline gap-3 px-3 py-1.5">
                <span className="w-14 shrink-0 font-mono text-[0.65rem] uppercase tracking-wider text-chalk-dim">
                  R{p.round}·{p.pickInRound}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm">
                  {p.playerName}
                  <span className="ml-1.5 font-mono text-[0.6rem] uppercase text-chalk-dim">
                    {p.position}
                  </span>
                </span>
                {p.round === 1 && (
                  <span className="shrink-0 font-mono text-[0.6rem] uppercase tracking-wider text-brass">
                    first rounder
                  </span>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      <Career name={name} season={data.season} />
    </div>
  );
}

/** Their current lineup, from the newest week we hold rosters for. */
function Roster({
  season,
  ownerId,
  data,
}: {
  season: number;
  ownerId: string;
  data: SeasonContext['stored']['bundle']['data'];
}) {
  const [lineups, setLineups] = useState<StoredLineups | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  // Rosters are only ever stored for the season being played — syncSeason skips
  // syncCurrentLineups for anything else — so an archived season has no lineups
  // and never will. The newest season the API offers is the live one.
  const { seasons } = useSeasonSelect();
  const isCurrentSeason = seasons === null || season >= Math.max(...seasons);

  const weeks = useMemo(() => {
    const board = scoreboardWeek(data);
    return [...new Set([data.currentWeek, board].filter((w): w is number => !!w))].sort(
      (a, b) => b - a,
    );
  }, [data]);

  useEffect(() => {
    let mounted = true;
    // Owner ids are stable across seasons, so without this the previous
    // season's roster keeps rendering under the new season's heading until the
    // fetch lands — or forever, if it hangs.
    setLineups(undefined);
    setError(null);
    (async () => {
      let failure: string | null = null;
      for (const week of weeks) {
        try {
          const got = await fetchLineups(season, week);
          if (!mounted) return;
          if (got) {
            setLineups(got);
            return;
          }
        } catch (e) {
          // Keep trying older weeks — a 500 on the newest shouldn't stop the
          // fallback — but remember that this was a failure, not an absence.
          failure = e instanceof Error ? e.message : 'Could not load rosters';
        }
      }
      if (!mounted) return;
      // "No rosters stored yet" is a claim about the data. Don't make it on the
      // strength of a request that never got an answer.
      if (failure) setError(failure);
      else setLineups(null);
    })();
    return () => {
      mounted = false;
    };
  }, [season, weeks]);

  if (error) {
    return (
      <Section title="Roster">
        <p className="font-mono text-xs text-blood">Couldn't load the roster: {error}</p>
      </Section>
    );
  }
  if (lineups === undefined) {
    return (
      <Section title="Roster">
        <p className="font-mono text-xs text-chalk-dim">loading…</p>
      </Section>
    );
  }
  const team = lineups?.teams.find((t) => t.ownerId === ownerId);
  if (!team) {
    return (
      <Section title="Roster">
        <p className="font-mono text-xs text-chalk-dim">
          {isCurrentSeason
            ? 'No rosters stored yet — they arrive with the next sync.'
            : `Rosters aren't kept for past seasons — only the season being played.`}
        </p>
      </Section>
    );
  }

  return (
    <Section
      title={`Roster · week ${lineups?.week}`}
      hint="dimmed figures are projections, until they play"
    >
      <div className="card grid gap-x-8 gap-y-0.5 p-4 sm:grid-cols-2">
        <div>
          {team.starters.map((p) => (
            <PlayerRow key={p.playerId} player={p} />
          ))}
        </div>
        <div className="mt-3 opacity-60 sm:mt-0">
          <p className="eyebrow mb-1">Bench</p>
          {team.bench.map((p) => (
            <PlayerRow key={p.playerId} player={p} />
          ))}
        </div>
      </div>
    </Section>
  );
}

function PlayerRow({ player }: { player: PlayerLine }) {
  return (
    <div className="flex items-baseline gap-2 py-0.5 font-mono text-xs">
      <span className="w-9 shrink-0 text-[0.65rem] uppercase tracking-wider text-chalk-dim">
        {player.slot}
      </span>
      <span className="min-w-0 flex-1 truncate font-body text-chalk">
        {player.name}
        <span className="ml-1.5 text-[0.6rem] uppercase tracking-wide text-chalk-dim">
          {player.slot === player.position ? player.proTeam : `${player.position} ${player.proTeam}`}
        </span>
      </span>
      {/* A projection and a result must not look the same. During a live slate
          this column holds both, and an unplayed player showing 14.2 in the
          same weight as somebody who actually scored it reads as points on the
          board. Dimmed here, as on the scoreboard's lineups. */}
      <span
        className={`w-11 shrink-0 text-right tabular ${
          player.hasScore ? 'text-chalk' : 'text-chalk-dim'
        }`}
      >
        {player.hasScore ? points(player.points) : points(player.projected)}
      </span>
    </div>
  );
}

/**
 * Everything before this season, from the checked-in history.
 *
 * Matched on name, which is the only bridge between the transcribed PDFs and
 * ESPN's owner ids. Silence beats a guess here: crediting somebody else's title
 * to you would be worse than showing nothing.
 */
function Career({ name, season }: { name: string; season: number }) {
  // Bounded to seasons before the one being viewed: opening the 2023 board and
  // clicking a manager should show what they had done by 2023, not credit them
  // with a title they went on to win two years later.
  const line = useMemo(
    () =>
      ownerRecords(1, season).find(
        (r) => r.owner.trim().toLowerCase() === name.trim().toLowerCase(),
      ),
    [name, season],
  );
  if (!line) return null;

  return (
    <Section title="Before this season">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Seasons" value={String(line.seasons)} />
        <Stat
          label="All-time"
          value={record(line.wins, line.losses, line.ties)}
          hint={`${(line.winPct * 100).toFixed(1)}%`}
        />
        <Stat label="Titles" value={String(line.titles)} tone={line.titles > 0 ? 'good' : undefined} />
        <Stat label="Podiums" value={String(line.podiums)} />
        <Stat label="Best finish" value={ordinal(line.bestFinish)} />
        <Stat label="Average finish" value={line.avgFinish.toFixed(1)} />
        <Stat
          label="Last places"
          value={String(line.lastPlaces)}
          tone={line.lastPlaces > 0 ? 'bad' : undefined}
        />
        <Stat
          label="Best season"
          value={points(line.bestSeasonPf.pf)}
          hint={String(line.bestSeasonPf.year)}
        />
      </div>
    </Section>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-baseline gap-x-3">
        <h2 className="eyebrow">{title}</h2>
        {hint && <span className="font-mono text-[0.6rem] text-chalk-dim">{hint}</span>}
      </div>
      {children}
    </section>
  );
}

function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'good' | 'bad';
}) {
  return (
    <div className="card p-3">
      <p className="font-mono text-[0.6rem] uppercase tracking-wider text-chalk-dim">{label}</p>
      <p
        className={`font-mono text-lg font-bold tabular ${
          tone === 'good' ? 'text-cash' : tone === 'bad' ? 'text-blood' : 'text-chalk'
        }`}
      >
        {value}
      </p>
      {hint && <p className="truncate font-mono text-[0.6rem] text-chalk-dim">{hint}</p>}
    </div>
  );
}
